import React, { useEffect, useState } from "react";
import ParticipantView from "./ParticipantView";
import { useClassRoom } from "../../../Hooks/ClassRoom";
import { Tooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import { logo } from "../../../assets";
import { useContentSetting } from "../../../Hooks/ContentSetting";

const IconButton = ({ tooltip, onClick, icon, className = "" }) => (
  <Tooltip title={tooltip}>
    <button className={`success-call-box ${className}`} onClick={onClick}>
      <Icon fontSize="17" className="start-call-icon" icon={icon} />
    </button>
  </Tooltip>
);

const CallControls = ({
  callStatus,
  isPlaying,
  isSharedScreen,
  onToggleMic,
  onToggleAudio,
  onToggleScreenShare,
  onEndCall,
  onAcceptCall,
  userInfo,
  handleToggleCam,
  isPaused,
  isMuted,
}) => {
  const isStudent = userInfo.user_type === "student";

  const getCallEndTooltip = () => {
    switch (callStatus) {
      case "incoming":
        return "Decline Call";
      case "accepted":
        return "End Call";
      default:
        return "Cancel Call";
    }
  };

  return (
    <div className="icon-box">
      {callStatus === "incoming" ? (
        <IconButton
          tooltip="Join Class"
          onClick={onAcceptCall}
          icon="carbon:phone-incoming"
        />
      ) : (
        <IconButton
          tooltip={isMuted ? "Unmute" : "Mute"}
          onClick={onToggleMic}
          icon={isMuted ? "vaadin:mute" : "bi:mic-fill"}
        />
      )}

      {callStatus === "accepted" ? (
        <IconButton
          tooltip={
            isStudent
              ? `${isPaused ? "Turn On Camera" : "Turn Off Camera"}`
              : `${isSharedScreen ? "Stop Sharing" : "Share Screen"}`
          }
          onClick={isStudent ? handleToggleCam : onToggleScreenShare}
          icon={
            isStudent
              ? `${isPaused ? "mdi:camera-off" : "ic:round-camera-alt"}`
              : `${
                  isSharedScreen
                    ? "fluent:share-screen-stop-28-filled"
                    : "fluent:share-screen-start-28-filled"
                }`
          }
        />
      ) : (
        <IconButton
          tooltip={isPlaying ? "Mute Audio" : "Unmute Audio"}
          onClick={onToggleAudio}
          icon={isPlaying ? "fa-solid:volume-up" : "fa-solid:volume-mute"}
        />
      )}
      <IconButton
        tooltip={getCallEndTooltip()}
        onClick={onEndCall}
        icon="subway:call-3"
        className="error"
      />
    </div>
  );
};

const LogoView = ({ userInfo, selectedUser }) => (
  <div className="logo-wrapper">
    <div className="image-box">
      <img src={logo} alt="App Logo" loading="lazy" decoding="async" />
    </div>
    {userInfo.user_type !== "student" && (
      <div className="student-calling-name">{selectedUser.name}</div>
    )}
  </div>
);

export default function MeetingView({ setBoxType, boxType }) {
  const {
    callUser,
    startCallTune,
    lastCallID,
    localUser,
    toggleScreenShare,
    isSharedScreen,
    selectedUser,
    acceptCall,
    callStatus,
    handleToggleCam,
    remoteStreams,
    updateLocalUser,
    isPaused,
    cleanupCallLocally,
  } = useClassRoom();

  const remoteStream = remoteStreams?.length > 0 ? remoteStreams[0] : null;
  const { stream: localStream, isMuted } = localUser;

  const { userInfo, socket } = useContentSetting();
  const [isPlaying, setIsPlaying] = useState(true);

  const handleToggleMic = () => {
    if (!localStream) return;

    const audioTracks = localStream.getAudioTracks();
    audioTracks.forEach((track) => (track.enabled = !track.enabled));
    socket.emit("changed_media", {
      to: callUser,
      from: userInfo._id,
      last_call_id: lastCallID,
      media_type: "is_muted",
      media_status: !isMuted,
    });
    updateLocalUser({ isMuted: !isMuted });
  };

  const handleEndCall = () => {
    let event = "call:canceled";
    if (callStatus === "incoming") {
      event = "call:declined";
    } else if (callStatus === "accepted" || callStatus === "accepting") {
      event = "call:ended";
    }

    socket.emit(event, {
      to: callUser,
      from: userInfo._id,
      last_call_id: lastCallID,
    });
    cleanupCallLocally({ playEndedTune: true });
  };

  const handleToggleAudio = () => {
    if (!startCallTune) return;

    if (!startCallTune.paused) {
      setIsPlaying(false);
      startCallTune.pause();
    } else {
      setIsPlaying(true);
      startCallTune.play();
    }
  };

  useEffect(() => {
    const handleNoAnswered = () => {
      let socketData = {
        to: callUser,
        from: userInfo._id,
        last_call_id: lastCallID,
      };
      socket.emit("call:no_answered", socketData);
    };

    let timeoutId;
    if (callStatus === "incoming") {
      timeoutId = setTimeout(() => {
        if (callStatus !== "accepted") {
          handleNoAnswered();
        }
      }, 40000);
    }
    if (callStatus === "accepted") {
      clearTimeout(timeoutId);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [callStatus, callUser, lastCallID, socket, userInfo._id]);

  const renderContent = () => {
    if (!lastCallID) {
      return <LogoView userInfo={userInfo} selectedUser={selectedUser} />;
    }

    return remoteStream ? (
      <ParticipantView setBoxType={setBoxType} boxType={boxType} />
    ) : (
      <LogoView userInfo={userInfo} selectedUser={selectedUser} />
    );
  };

  return (
    <>
      {renderContent()}

      {boxType !== "CIRCLE" && (
        <CallControls
          callStatus={callStatus}
          isMuted={isMuted}
          isPlaying={isPlaying}
          isSharedScreen={isSharedScreen}
          onToggleMic={handleToggleMic}
          onToggleAudio={handleToggleAudio}
          onToggleScreenShare={() => toggleScreenShare(!isSharedScreen)}
          onEndCall={handleEndCall}
          onAcceptCall={acceptCall}
          userInfo={userInfo}
          handleToggleCam={handleToggleCam}
          isPaused={isPaused}
        />
      )}
    </>
  );
}
